interface EndpointOptions {
	rateLimit: number;
	params: Record<string, string>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Endpoint<T = any> {
	private readonly endpoint: string;
	private readonly options: EndpointOptions;

	public isLoading = $state(false);
	public isWaiting = $state(false);
	public data?: T = $state(undefined);

	private lastRequest = -1;
	private queuedRequest?: { params: URLSearchParams };
	private isStarted = false;
	private currentAnimationFrameId = -1;

	constructor(endpoint: string, options?: Partial<EndpointOptions>) {
		this.endpoint = endpoint;

		this.options = {
			rateLimit: 0,
			params: {},
			...options
		};

		$effect(() => {
			this.start();
			return this.stop;
		});
	}

	private start = () => {
		if (this.isStarted) return;
		this.isStarted = true;
		requestAnimationFrame(this.loop);
	};

	private stop = () => {
		if (!this.isStarted) return;
		this.isStarted = false;
		cancelAnimationFrame(this.currentAnimationFrameId);
	};

	private loop = () => {
		if (!this.isStarted) return;
		this.isWaiting = this.queuedRequest !== undefined || this.isLoading;
		if (this.queuedRequest && this.canFetch()) {
			this.fetch(this.queuedRequest.params);
		}
		cancelAnimationFrame(this.currentAnimationFrameId);
		this.currentAnimationFrameId = requestAnimationFrame(this.loop);
	};

	private canFetch() {
		if (this.isLoading) return false;
		if (this.lastRequest !== -1 && this.options.rateLimit > 0) {
			const delta = Date.now() - this.lastRequest;
			if (delta < this.options.rateLimit) {
				return false;
			}
		}
		return true;
	}

	public async fetch(params?: URLSearchParams): Promise<T | undefined> {
		params = params ?? new URLSearchParams();
		if (!this.canFetch()) {
			this.queuedRequest = { params };
			return;
		}
		this.queuedRequest = undefined;

		params = new URLSearchParams({ ...this.options.params, ...Object.fromEntries(params) });

		const url = `${this.endpoint}?${params.toString()}`;
		this.isLoading = true;
		this.lastRequest = Date.now();

		const response = await fetch(url);
		const json = await response.json();
		this.isLoading = false;
		if (json.code !== 200) return;

		this.data = json.data;
		this.isLoading = false;

		return this.data;
	}
}
